import { Component, Injector, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { LazyLoadEvent, MenuItem } from 'primeng/api';
import { filter, finalize } from 'rxjs/operators';
import { AppComponentBase, PagedTableSummary } from 'src/app/app-component-base';
import {
    DynamicCatalogGetVistaInfoQuery,
    DynamicCatalogServiceProxy,
    DataListQuery,
    DynamicCatalogViewInfoResponse,
    DynamicCatalogDefinitionResponse,
    DynamicCatalogDeleteCommand
} from '../../../shared/service-proxies/service-proxies';
import { DateTimeService } from '../../../shared/services/datetime.service';
import { AppComponent } from '../../../app/app.component';
import { DynamicCatalogFormModalComponent } from './dynamiccatalog-form-modal.component';
import * as moment from 'moment';
import { DecimalPipe } from '@angular/common';
// import { DecimalPipe } from '@angular/common';

@Component({
    selector: 'app-dynamiccatalog-view',
    encapsulation: ViewEncapsulation.None,
    templateUrl: './dynamicCatalog-view.component.html',
    styleUrls: ['../../../assets/css-custom/p-datatable-general.scss'],
    providers: [
        DecimalPipe
    ]
})
export class DynamicCatalogViewComponent extends AppComponentBase implements OnInit {

    @ViewChild('dt1', { static: false }) table;

    // Parámetros necesarios para el componente
    @Input() tabla; // El catálogo que se va a administrar desde aquí
    @Input() urlCrear: string;
    @Input() urlEditar: string;
    @Input() permisos: any;

    form: FormGroup;

    data: any[];
    pagedTableSummary: PagedTableSummary = new PagedTableSummary();

    selectedItem: any;
    items: MenuItem[];
    query: DataListQuery = new DataListQuery();

    browserStorageTableKey: string;
    browserStorageTableFilterKey: string;

    tituloVista: string;
    claveAyudaVista: string;
    viewForm: DynamicCatalogViewInfoResponse;
    cols: any[] = [];

    constructor(
        injector: Injector,
        private formBuilder: FormBuilder,
        private router: Router,
        private service: DynamicCatalogServiceProxy,
        private app: AppComponent,
        private dateTimeService: DateTimeService,
        private decimalPipe: DecimalPipe
    ) {
        super(injector);
    }

    // convenience getter for easy access to form fields
    get f() {
        return this.form.controls;
    }

    ngOnInit() {
        const self = this;

        self.form = self.formBuilder.group({
            filterText: [null]
        });

        self.refresh();
    }

    refresh(): void {
        const self = this;

        setTimeout(function () {

            // Se inicializan las columnas
            self.cols = [];

            self.browserStorageTableFilterKey = 'table-' + self.tabla + 'filters';
            self.browserStorageTableKey = self.browserStorageService.calculateKey('table-' + self.tabla);

            self.query.pageNumber = 0;
            self.query.pageSize = 10;

            // Asigno los filtro guardados en localStorage
            const filters: any = self.getFilters(self.browserStorageTableFilterKey);
            if (filters) {
                self.query = filters;
            }

            self.f.filterText.setValue(filters.filter);

            self.tituloVista = self.l('Catalogos.' + self.tabla + '.Titulo');
            self.claveAyudaVista = 'CATALOGOS.' + self.tabla.toUpperCase() + '.VIEW';

            // Se invoca el método principal del catálogo dinámico
            self.getDefiniciones();
        }, 0);
    }

    getDefiniciones(): void {
        const self = this;

        const req = new DynamicCatalogGetVistaInfoQuery();
        req.tabla = self.tabla;

        self.app.blocked = true;

        self.service.getDynamicCatalogVistaDefinitionsList(req)
            .pipe(finalize(() => {
                self.app.blocked = false;
            }))
            .subscribe(data => {
                self.viewForm = data;

                self.prepararColumnasYEtiquetas();

                // Una vez traídas las definiciones, debemos ir por la lista de registros del catálogos
                self.getList();
            });
    }

    filterSearch(event: any): void {
        const self = this;

        event.preventDefault();

        self.browserStorageService.remove(self.browserStorageTableKey);
        self.query.pageNumber = 0;
        self.table.reset();
    }

    getList(): void {
        const self = this;

        self.query.tabla = self.tabla;
        self.query.filter = self.f.filterText.value;
        self.query.pageNumber = self.query.pageNumber ? self.query.pageNumber : 0;
        self.query.pageSize = self.query.pageSize ? self.query.pageSize : 10;

        self.app.blocked = true;

        self.service.getList(self.query)
            .pipe(finalize(() => {
                self.app.blocked = false;
            }))
            .subscribe(data => {
                self.data = data;

                let total = 0;
                if (data.length > 0) {
                    total = data[0]['total'];
                }

                // Calculate paged table summary
                self.pagedTableSummary = self.calculateTableSummay(total, self.query.pageNumber, self.query.pageSize);

                // Asigno filtros para que se queden guardados en localStorage
                self.browserStorageService.set(self.browserStorageTableFilterKey, self.query);
            });
    }

    prepararColumnasYEtiquetas(): void {
        const self = this;

        self.cols = [];

        for (let i = 0, len = self.viewForm.camposVistaList.length; i < len; i++) {

            const f = self.viewForm.camposVistaList[i];

            // Si el campo tiene una relación a otra tabla, entonces no se muestra en la vista
            // porque es un Id, ejemplo PaisId
            const rel = self.viewForm.relationsList.find(m => m.campoRelacion === f);
            if (!rel) {

                // Revisar si el campo "existe" en las definiciones y si se debe mostrar en la vista
                const ex = self.viewForm.definitionsList.find(m => m.campo === f);
                if (!ex || (ex && ex.mostrarEnVista)) {
                    self.cols.push({
                        field: f,
                        header: this.l('Catalogos.' + self.tabla + '.' + f + '.Col')
                    });
                }
            }
        }
    }

    getCellValue(objRef: any, field: string): string {
        const self = this;

        const def = self.viewForm.definitionsList.find(m => m.campo === field);
        const tipo = self.getTipoControl(field);

        let resp = '';

        if (tipo === 'numberU') {
            resp = objRef[field];
        } else if (tipo === 'numberF') {
            // Hay que formatear el número a los decimales indicados
            resp = self.decimalPipe.transform(objRef[field], objRef['decimales']);
        } else if (tipo === 'boolean') {
            // Es booleano
            if (objRef[field] === true) {
                resp = '<span class="p-false-button p-false-button-sm-size">' + self.l('Yes') + '</span>';
            } else {
                resp = '<span class="p-false-button p-false-button-default p-false-button-sm-size p-button-danger">' +
                    self.l('No') + '</span>';
            }
        } else if (tipo === 'date') {
            if (objRef[field]) {
                resp = self.dateTimeService.getDateToDisplay(moment(objRef[field]));
            } else {
                resp = '';
            }
        } else if (tipo === 'datetime') {
            if (objRef[field]) {
                resp = self.dateTimeService.getDateTimeToDisplay(moment(objRef[field]));
            } else {
                resp = '';
            }
        } else if (tipo === 'text') {
            resp = objRef[field];
        }

        return resp;
    }

    getTipoControl(field: string): string {
        const self = this;

        const numbersUnformat: string[] = ['int', 'tinyint', 'smallint', 'bigint'];
        const numbersformat: string[] = ['decimal', 'float', 'money'];
        const booleans: string[] = ['bit'];
        const date: string[] = ['date'];
        const datetime: string[] = ['datetime'];

        let resp = 'text';
        const def = self.viewForm.definitionsList.find(m => m.campo === field);

        if (!def) {
            return resp;
        }

        const isNumberUnformat = numbersUnformat.indexOf(def.tipo) >= 0;
        const isNumberformat = numbersformat.indexOf(def.tipo) >= 0;
        const isBoolean = booleans.indexOf(def.tipo) >= 0;
        const isDate = date.indexOf(def.tipo) >= 0;
        const isDateTime = datetime.indexOf(def.tipo) >= 0;

        // Es número
        if (isNumberUnformat) {
            resp = 'numberU';
        } else if (isNumberformat) {
            resp = 'numberF';
        } else if (isBoolean) {
            resp = 'boolean';
        } else if (isDate) {
            // Hay que mostrar un control "calendar"
            resp = 'date';
        } else if (isDateTime) {
            resp = 'datetime';
        } else {
            resp = 'text';
        }

        return resp;
    }

    calculateTableSummay(totalRecords: number, pageNumber: number, pageSize: number): PagedTableSummary {
        const pagedTableSummary: PagedTableSummary = new PagedTableSummary();

        pagedTableSummary.totalRecords = totalRecords;

        if (pagedTableSummary.totalRecords > 0) {
            pagedTableSummary.firstRecordInPage = (pageNumber * pageSize) + 1;
            pagedTableSummary.lastRecordInPage = pagedTableSummary.firstRecordInPage + pageSize - 1;

            if (pagedTableSummary.lastRecordInPage > pagedTableSummary.totalRecords) {
                pagedTableSummary.lastRecordInPage = pagedTableSummary.totalRecords;
            }
        }

        return pagedTableSummary;
    }

    loadData(event: LazyLoadEvent) {
        const self = this;

        if (event.sortField) {
            self.query.sorting = event.sortField + ' ' + (event.sortOrder === 1 ? 'ASC' : 'DESC');
        } else {
            self.query.sorting = '';
        }

        self.query.pageNumber = 0 + (event.first / event.rows);
        self.query.pageSize = event.rows;

        self.getList();
    }

    create(): void {
        if (this.urlCrear) {
            this.router.navigate([this.urlCrear]);
        } else {
            this.createInModal();
        }
    }

    edit(rowData: any): void {
        const self = this;
        const id = rowData[self.viewForm.campoLlavePrimaria];
        if (this.urlEditar) {
            this.router.navigate([this.urlEditar, id]);
        } else {
            this.editInModal(id);
        }
    }

    createInModal(): void {
        const self = this;
        const ref = self.dialogService.open(DynamicCatalogFormModalComponent, {
            styleClass: 'd-sm d-md-65 d-lg-70 d-xl-50',
            header: self.l('Catalogos.' + self.tabla + '.Nuevo.Titulo'),
            showHeader: true,
            dismissableMask: false,
            // footer: 'DUMMY',
            data: {
                tabla: self.tabla,
                permisos: self.permisos
            }
        });

        ref.onClose.subscribe((isSaved: boolean) => {
            if (isSaved) {
                self.getList();
            }
        });
    }

    editInModal(id: any): void {
        const self = this;

        const ref = self.dialogService.open(DynamicCatalogFormModalComponent, {
            styleClass: 'd-sm d-md-65 d-lg-70 d-xl-50',
            header: self.l('Catalogos.' + self.tabla + '.Edicion.Titulo'),
            showHeader: true,
            dismissableMask: false,
            // footer: 'DUMMY',
            data: {
                tabla: self.tabla,
                id: id,
                permisos: self.permisos
            }
        });

        ref.onClose.subscribe((isSaved: boolean) => {
            if (isSaved) {
                self.getList();
            }
        });
    }

    delete(rowData: any): void {
        const self = this;

        self.alertService.confirm(self.l('ConfirmationDeleteRecordMessage'), self.l('Confirmation'),
            (resp) => {
                if (resp === true) {
                    self.app.blocked = true;

                    const id = rowData[self.viewForm.campoLlavePrimaria];

                    self.service.delete(new DynamicCatalogDeleteCommand({ tabla: self.tabla, id: id }))
                        .pipe(finalize(() => {
                            self.app.blocked = false;
                        }))
                        .subscribe(data => {
                            self.notify.success(self.l('RecordDeletedSuccessful'), self.l('Success'));
                            self.getList();
                        });
                }
            });
    }
}
